import React from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../../i18n/constants/keys';

interface IHighlights {
  data: string[];
}

export const Highlights: React.FC<IHighlights> = ({ data }) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  // Gradient colors for different highlights
  const gradients = [
    'from-blue-50 to-indigo-50',
    'from-purple-50 to-pink-50',
    'from-green-50 to-emerald-50',
    'from-orange-50 to-amber-50',
    'from-teal-50 to-cyan-50',
    'from-rose-50 to-red-50',
  ];

  const accentColors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-green-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-rose-500',
  ];

  const iconColors = [
    'text-blue-500',
    'text-purple-500',
    'text-green-500',
    'text-orange-500',
    'text-teal-500',
    'text-rose-500',
  ];

  if (!data || data.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-16 px-4'>
        <div className='w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4'>
          <svg
              className='w-10 h-10 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z'
            />
          </svg>
        </div>
        <h3 className='text-lg font-semibold text-gray-600 mb-2'>
          { t(SI.results.userDetails.highlights.noHighlights) }
        </h3>
        <p className='text-sm text-gray-500 text-center max-w-md'>
          { t(SI.results.userDetails.highlights.noHighlightsDesc) }
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      { data.map((item, index) => {
        const gradientClass = gradients[index % gradients.length];
        const accentClass = accentColors[index % accentColors.length];
        const iconColorClass = iconColors[index % iconColors.length];

        return (
          <div
              key={index}
              className={`group relative bg-gradient-to-r ${gradientClass} rounded-xl p-5 border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-md`}
          >
            { /* Left accent bar */ }
            <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${accentClass} rounded-l-xl`}
            ></div>

            { /* Content */ }
            <div className='flex items-start gap-4 pl-3'>
              { /* Icon */ }
              <div className='flex-shrink-0 mt-0.5'>
                <div className='w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-200'>
                  <svg
                      className={`w-5 h-5 ${iconColorClass}`}
                      fill='currentColor'
                      viewBox='0 0 20 20'
                  >
                    <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                        clipRule='evenodd'
                    />
                  </svg>
                </div>
              </div>

              { /* Text content */ }
              <div className='flex-1 min-w-0'>
                <p className='text-gray-800 leading-relaxed text-sm'>{ item }</p>
              </div>

              { /* Number badge */ }
              <div className='flex-shrink-0'>
                <div
                    className={`w-7 h-7 ${accentClass} bg-opacity-10 rounded-full flex items-center justify-center`}
                >
                  <span className={`text-xs font-bold ${iconColorClass}`}>
                    { index + 1 }
                  </span>
                </div>
              </div>
            </div>

            { /* Decorative gradient on hover */ }
            <div className='absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-b-xl'></div>
          </div>
        );
      }) }
    </div>
  );
};
